/**
 * Phase 10b — Bloc "Marge sur achats" (Admin+Gestionnaire only, jamais exposé publiquement).
 *
 * Libellé produit obligatoire : "Marge sur achats" (JAMAIS "Marge réelle").
 *
 * Phase Rentab-1+2-B (13/09/2026) — Refonte 4 termes :
 *   marge = vendu − matériel − main-d'œuvre − sous-traitance
 * + badge visuel "Prévisionnelle" (0 facture) / "Consolidée" (≥1 facture)
 * + libellé "N devis + M avenants signés" (décomposition explicite)
 *
 * Peut s'afficher :
 *  - `variant="full"` : bloc complet dans l'onglet "Coûts fournisseurs" (défaut)
 *  - `variant="compact"` : KPI compact panneau latéral fiche chantier
 *
 * Si aucun devis signé n'est présent : affichage "Non calculable" (pas de division par 0).
 */
import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, TrendingUp, TrendingDown, Wallet, Info } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const money = (n) =>
  new Intl.NumberFormat("fr-BE", { style: "currency", currency: "EUR" }).format(Number(n) || 0);
const pct = (n) => `${(Number(n) || 0).toLocaleString("fr-BE", { maximumFractionDigits: 1 })} %`;

/**
 * Libellé "N devis + M avenants signés" (jamais "N devis signés" en concaténant).
 * Défaut 1 Option B — décomposition explicite pour lever l'ambiguïté PO.
 */
const buildSignedLabel = (parents, amendments) => {
  const p = Number(parents) || 0;
  const a = Number(amendments) || 0;
  if (p === 0 && a === 0) return "Aucun devis signé";
  const pPart = `${p} devis`;
  if (a === 0) return `${pPart} signé${p > 1 ? "s" : ""}`;
  const aPart = `${a} avenant${a > 1 ? "s" : ""}`;
  return `${pPart} + ${aPart} signé${p + a > 1 ? "s" : ""}`;
};

export default function ChantierMarginBlock({ chantierId, variant = "full", refreshKey = 0 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setForbidden(false);
    try {
      const { data } = await api.get(`/chantiers/${chantierId}/margin`);
      setData(data);
    } catch (e) {
      // 403 = utilisateur non-admin → on ne rend rien (rôle commercial ne voit pas la marge)
      if (e?.response?.status === 403) {
        setForbidden(true);
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setLoading(false);
    }
  }, [chantierId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (loading) {
    return variant === "compact" ? null : (
      <div className="rounded border border-slate-800 bg-slate-900/40 p-4" data-testid="margin-loading">
        <Loader2 className="h-4 w-4 animate-spin text-slate-500" />
      </div>
    );
  }
  // Non-admin (ex: commercial) → composant totalement invisible
  if (forbidden || !data) return null;

  const computable = data.computable;
  const marginHTVA = data.margin_htva || 0;
  const marginPct = data.margin_percent || 0;
  const isPositive = marginHTVA >= 0;

  // === Variante COMPACTE (KPI panneau latéral) =============================
  if (variant === "compact") {
    return (
      <div
        className="rounded border border-slate-800 bg-slate-900/60 p-3"
        data-testid="chantier-sidebar-margin-kpi"
      >
        <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-slate-500">
          <Wallet className="h-3 w-3 text-[#C4553B]" />
          Marge sur achats
        </div>
        {computable ? (
          <>
            <div
              className={`mt-1 text-lg font-semibold tabular-nums ${
                isPositive ? "text-emerald-300" : "text-red-400"
              }`}
              data-testid="chantier-sidebar-margin-value"
            >
              {isPositive ? "+" : ""}
              {money(marginHTVA)}
            </div>
            <div className="text-xs text-slate-500">
              {isPositive ? (
                <TrendingUp className="mr-1 inline h-3 w-3" />
              ) : (
                <TrendingDown className="mr-1 inline h-3 w-3" />
              )}
              {pct(marginPct)} · {data.supplier_invoices_count} facture(s)
            </div>
            <div className="mt-1 text-[10px] leading-tight text-slate-600">
              Hors main-d&apos;œuvre et frais généraux.
            </div>
          </>
        ) : (
          <div className="mt-1 text-xs italic text-slate-500" data-testid="chantier-sidebar-margin-non-calculable">
            Non calculable — aucun devis signé.
          </div>
        )}
      </div>
    );
  }

  // === Variante FULL (onglet coûts fournisseurs) ===========================
  const signedLabel = buildSignedLabel(
    data.signed_parents_count,
    data.signed_amendments_count
  );
  const isConsolidated = (data.supplier_invoices_count || 0) >= 1;
  return (
    <div
      className="rounded border border-slate-700 bg-gradient-to-br from-slate-900/80 to-slate-950/60 p-4"
      data-testid="chantier-margin-block-admin"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Wallet className="h-5 w-5 text-[#C4553B]" />
          <div>
            <div className="text-sm font-semibold text-slate-100">Marge sur achats</div>
            <div className="text-xs text-slate-500">Admin & gestionnaire — donnée interne</div>
          </div>
        </div>
        {computable && (
          <Badge
            className={
              isConsolidated
                ? "border-emerald-600/60 bg-emerald-500/10 text-emerald-300"
                : "border-slate-600/60 bg-slate-700/20 text-slate-300"
            }
            data-testid={isConsolidated ? "margin-badge-consolidated" : "margin-badge-forecast"}
          >
            {isConsolidated ? "Consolidée" : "Prévisionnelle"}
          </Badge>
        )}
      </div>

      {computable ? (
        <>
          {/* Bandeau récap — 5 blocs */}
          <div className="mt-3 grid gap-3 sm:grid-cols-5">
            <div>
              <div className="text-xs uppercase tracking-wide text-slate-500">Devis signés HTVA</div>
              <div className="mt-0.5 tabular-nums text-slate-100" data-testid="margin-signed-quotes-htva">
                {money(data.total_signed_quotes_htva)}
              </div>
              <div className="text-[10px] text-slate-500" data-testid="margin-signed-label">
                {signedLabel}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-slate-500">Matériel HTVA</div>
              <div
                className="mt-0.5 tabular-nums text-slate-100"
                data-testid="margin-material-htva"
              >
                − {money(data.total_material_htva || 0)}
              </div>
              <div className="text-[10px] text-slate-500">Factures fournisseurs</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-slate-500">Main-d&apos;œuvre</div>
              <div
                className="mt-0.5 tabular-nums text-slate-100"
                data-testid="margin-labor-cost"
              >
                − {money(data.labor_cost || 0)}
              </div>
              <div className="text-[10px] text-slate-500">
                {data.labor_cost_source === "actual"
                  ? "jours réalisés"
                  : data.labor_cost_source === "estimated"
                  ? "jours prévus"
                  : data.labor_cost_source === "no_daily_rate"
                  ? "taux non défini"
                  : "—"}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-slate-500">Sous-traitance</div>
              <div
                className="mt-0.5 tabular-nums text-slate-100"
                data-testid="margin-subcontracting-cost"
              >
                − {money(data.subcontracting_cost_retained || 0)}
              </div>
              <div className="text-[10px] text-slate-500">
                {data.subcontracting_cost_mode === "actual_finalized"
                  ? "réel (finalisé)"
                  : data.subcontracting_cost_mode === "max_pessimistic"
                  ? "MAX prévu/réel"
                  : data.subcontracting_cost_mode === "estimated_only"
                  ? "prévu seul"
                  : data.subcontracting_cost_mode === "actual_only"
                  ? "réel seul"
                  : "—"}
              </div>
            </div>
            <div className="rounded border border-slate-700/60 bg-slate-950/40 p-2">
              <div className="text-xs uppercase tracking-wide text-slate-500">Marge sur achats</div>
              <div
                className={`mt-0.5 text-2xl font-bold tabular-nums ${
                  isPositive ? "text-emerald-300" : "text-red-400"
                }`}
                data-testid="margin-value"
              >
                {isPositive ? "+" : ""}
                {money(marginHTVA)}
              </div>
              <div
                className={`text-xs ${isPositive ? "text-emerald-400/80" : "text-red-400/80"}`}
                data-testid="margin-percent"
              >
                {isPositive ? (
                  <TrendingUp className="mr-1 inline h-3 w-3" />
                ) : (
                  <TrendingDown className="mr-1 inline h-3 w-3" />
                )}
                {pct(marginPct)}
              </div>
            </div>
          </div>
          <div
            className="mt-4 flex items-start gap-1.5 rounded border border-slate-800 bg-slate-950/50 p-2 text-[11px] text-slate-500"
            data-testid="margin-disclaimer"
          >
            <Info className="mt-0.5 h-3 w-3 flex-shrink-0" />
            <span>
              Calcul basé sur les devis signés, factures fournisseurs rattachées (hors rejetées),
              jours main-d&apos;œuvre × coût journalier, et sous-traitance retenue selon le
              mode pessimiste. Hors frais généraux.
            </span>
          </div>
        </>
      ) : (
        <div
          className="mt-3 rounded border border-dashed border-slate-800 bg-slate-950/30 p-4 text-sm italic text-slate-500"
          data-testid="margin-non-calculable"
        >
          Non calculable — aucun devis signé sur ce chantier pour l&apos;instant.
        </div>
      )}
    </div>
  );
}
